// hand/advance.ts - the core state-transition reducer. ENGINE_DESIGN.md §2.1.
import { STREET_ORDER } from '@postflop/schema';
import { legalActions } from './legal';
import { deriveStreetBettingState, streetActionsOf } from './betting';
import { buildPots } from './sidepots';
import { postflopOrder, preflopOrder } from './positions';
import { dealForSpec, BOARD_LENGTH_BY_STREET } from './deal';
import { awardUncontested, resolveShowdown } from './showdown';
const nextToActSeat = (state, actingSeat, n) => {
    const order = state.street === 'preflop' ? preflopOrder(n, state.buttonSeat) : postflopOrder(n, state.buttonSeat);
    const streetActions = streetActionsOf(state);
    const { currentBet, lastActionIndexBySeat } = deriveStreetBettingState(state.spec.stakes.bb, state.street, streetActions);
    const currentIdx = order.indexOf(actingSeat);
    for (let step = 1; step <= order.length; step++) {
        const seat = order[(currentIdx + step) % order.length];
        const p = state.players.find((pp) => pp.seat === seat);
        if (!p || p.folded || p.allIn)
            continue;
        const actedIdx = lastActionIndexBySeat.get(seat);
        const needsToAct = actedIdx === undefined || p.committedThisStreet < currentBet;
        if (needsToAct)
            return seat;
    }
    return null;
};
const beginStreet = (state, nextStreet, n) => {
    const { board } = dealForSpec(state.spec, n);
    const revealLength = BOARD_LENGTH_BY_STREET[nextStreet];
    const players = state.players.map((p) => ({ ...p, committedThisStreet: 0 }));
    const order = postflopOrder(n, state.buttonSeat);
    const firstToAct = order.find((seat) => {
        const p = players.find((pp) => pp.seat === seat);
        return p && !p.folded && !p.allIn;
    });
    return {
        ...state,
        street: nextStreet,
        board: board.slice(0, revealLength),
        players,
        currentBet: 0,
        minRaiseTo: state.spec.stakes.bb,
        toAct: firstToAct ?? null,
    };
};
/** Street just closed (toAct already set to null). Advance streets - running out the board
 *  without further betting once at most one non-folded player can still act - until either
 *  betting resumes or the hand reaches showdown. */
const closeStreetAndAdvance = (state, n) => {
    let s = state;
    for (;;) {
        const activeNonAllIn = s.players.filter((p) => !p.folded && !p.allIn);
        if (s.street === 'river' || activeNonAllIn.length <= 1) {
            if (s.street !== 'river') {
                const { board } = dealForSpec(s.spec, n);
                s = { ...s, board, street: 'river' };
            }
            return resolveShowdown(s);
        }
        const nextStreet = STREET_ORDER[STREET_ORDER.indexOf(s.street) + 1];
        s = beginStreet(s, nextStreet, n);
        if (s.toAct !== null)
            return s;
        // Everyone eligible to act on the new street is already folded/all-in - keep running out.
    }
};
export const advance = (state, action) => {
    if (state.phase !== 'acting' || state.toAct === null) {
        throw new Error('cannot act: hand is not awaiting an action');
    }
    if (action.seat !== state.toAct) {
        throw new Error(`action seat ${action.seat} does not match toAct ${state.toAct}`);
    }
    const legal = legalActions(state);
    const matching = legal.find((l) => l.kind === action.kind);
    if (!matching) {
        throw new Error(`illegal action: '${action.kind}' is not legal for seat ${action.seat}`);
    }
    if (matching.min !== undefined) {
        const amount = action.amount ?? matching.min;
        if (amount < matching.min || (matching.max !== undefined && amount > matching.max)) {
            throw new Error(`illegal amount ${amount} for '${action.kind}': expected [${matching.min}, ${matching.max}]`);
        }
    }
    const n = state.players.length;
    const player = state.players.find((p) => p.seat === action.seat);
    if (!player)
        throw new Error(`no player at seat ${action.seat}`);
    const applyCommit = (toAmount) => {
        const delta = toAmount - player.committedThisStreet;
        const stack = player.stack - delta;
        return { ...player, stack, committedThisStreet: toAmount, committedTotal: player.committedTotal + delta, allIn: stack <= 0 };
    };
    let normalizedAction = action;
    let players = state.players;
    switch (action.kind) {
        case 'fold':
            players = players.map((p) => (p.seat === action.seat ? { ...p, folded: true } : p));
            break;
        case 'check':
            break;
        case 'call':
        case 'jam': {
            const amount = matching.min; // both are fixed-amount actions (min === max)
            players = players.map((p) => (p.seat === action.seat ? applyCommit(amount) : p));
            normalizedAction = { ...action, amount };
            break;
        }
        case 'bet':
        case 'raise': {
            const amount = action.amount ?? matching.min;
            players = players.map((p) => (p.seat === action.seat ? applyCommit(amount) : p));
            normalizedAction = { ...action, amount };
            break;
        }
    }
    let next = {
        ...state,
        players,
        history: [...state.history, { street: state.street, action: normalizedAction }],
        pots: buildPots(players),
    };
    const unfolded = players.filter((p) => !p.folded);
    if (unfolded.length === 1) {
        return awardUncontested(next);
    }
    const streetActions = streetActionsOf(next);
    const { currentBet, minRaiseTo } = deriveStreetBettingState(state.spec.stakes.bb, state.street, streetActions);
    next = { ...next, currentBet, minRaiseTo };
    const nextSeat = nextToActSeat(next, action.seat, n);
    if (nextSeat !== null) {
        return { ...next, toAct: nextSeat };
    }
    return closeStreetAndAdvance({ ...next, toAct: null }, n);
};
//# sourceMappingURL=advance.js.map